import {
  ApiRoute,
  Newsletter,
  NewsletterSchema,
  PresignedReq,
  PresignedResp,
  PresignedRespSchema,
  PresignedType,
  SubscriptionReq,
  UPLOAD_ALLOWED_CONTENT_TYPES,
  UploadAllowedContentType,
  UploadImageResp,
  getApiRoute
} from "@humanex/interfaces";
import axios from "axios";

import { ApiClientConfig, BaseApiClient } from "./base-client";

type UploadProgress = {
  percent: number;
  loaded: number;
  total: number;
};

type UploadImageOptions = {
  onProgress?: (e: UploadProgress) => void;
} & (
  | { uploadType: PresignedType.USER_AVATAR; userId?: string | null }
  | { uploadType: PresignedType.ORG_AVATAR; orgId: string }
  | { uploadType: PresignedType.ORG_BANNER; orgId: string }
  | { uploadType: PresignedType.PROGRAM_BANNER; programId: string }
  | { uploadType?: PresignedType.UNKNOWN }
);

export class PublicApiClient extends BaseApiClient {
  constructor(config: ApiClientConfig) {
    super({
      ...config,
      baseEndpoint: getApiRoute(ApiRoute.PUBLIC),
      requireAuth: false
    });
  }

  async subscribeToNewsletter(request: SubscriptionReq): Promise<Newsletter> {
    return await this.post("subscribe", NewsletterSchema, request, {
      requireAuth: false
    });
  }

  private presigned = async (params: PresignedReq): Promise<PresignedResp> => {
    return await this.get("uploads/presigned", PresignedRespSchema, {
      requireAuth: true,
      params
    });
  };

  uploadImage = async (file: File, opts?: UploadImageOptions): Promise<UploadImageResp> => {
    if (!(UPLOAD_ALLOWED_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      throw new Error(`Unsupported file type: ${file.type}`);
    }
    const contentType = file.type as UploadAllowedContentType;
    const base = { fileSize: file.size, fileName: file.name, contentType };

    let req: PresignedReq;
    if (!opts?.uploadType || opts.uploadType === PresignedType.UNKNOWN) {
      req = { ...base, uploadType: PresignedType.UNKNOWN };
    } else {
      switch (opts.uploadType) {
        case PresignedType.USER_AVATAR:
          req = {
            ...base,
            uploadType: PresignedType.USER_AVATAR,
            userId: opts.userId
          };
          break;
        case PresignedType.ORG_AVATAR:
          req = {
            ...base,
            uploadType: PresignedType.ORG_AVATAR,
            orgId: opts.orgId
          };
          break;
        case PresignedType.ORG_BANNER:
          req = {
            ...base,
            uploadType: PresignedType.ORG_BANNER,
            orgId: opts.orgId
          };
          break;
        case PresignedType.PROGRAM_BANNER:
          req = {
            ...base,
            uploadType: PresignedType.PROGRAM_BANNER,
            programId: opts.programId
          };
          break;
      }
    }

    const presign = await this.presigned(req);

    await axios.put(presign.url, file, {
      headers: {
        "Content-Type": file.type
      },
      onUploadProgress: (e) => {
        if (!e.total) return;

        const percent = Math.round((e.loaded / e.total) * 100);

        opts?.onProgress?.({
          percent,
          loaded: e.loaded,
          total: e.total
        });
      }
    });

    const objUrl = presign.url.split("?")[0];
    if (!objUrl) throw new Error("Cannot parse url");

    return { url: objUrl, key: presign.key };
  };
}
